import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Typography } from '@carto/meridian-ds/components'
import { CheckOutlined } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'app/state'
import { setTakeOverResponseState } from 'features/builder/state/builderSlice'
import useTimer from 'features/common/hooks/useTimer'
import { useTakeoverResponseAction } from 'features/builder/hooks/collaboration'
import { TAKEOVER_TIMEOUT_SEC, TakeResponseDialogResult } from 'features/builder/types'
import { RequestStatus } from 'features/common'

export type BuilderTakeOverResponseDialogProps = {
  open: boolean
  onFinished: (result: TakeResponseDialogResult) => void
}

function BuilderTakeOverResponseDialog({ open, onFinished }: BuilderTakeOverResponseDialogProps) {
  const dispatch = useAppDispatch()

  const currentMap = useAppSelector((state) => state.maps.currentMap)
  const takeOverResponseState = useAppSelector((state) => state.builder.takeOverResponseState)

  const isSaving = useAppSelector((state) => state.maps.mapEditStatus === RequestStatus.Pending)

  const { mutateAsync: takeOverActionResponse } = useTakeoverResponseAction()
  const takeOverAutoAcceptDeadline = takeOverResponseState?.requestedTime
    ? takeOverResponseState?.requestedTime + TAKEOVER_TIMEOUT_SEC * 1000
    : undefined
  const timeLeft = useTimer(takeOverAutoAcceptDeadline)

  const handleAccept = async () => {
    if (!currentMap) return

    await takeOverActionResponse({ mapId: currentMap.id, action: 'accept' })

    dispatch(setTakeOverResponseState({ ...takeOverResponseState, accepted: true }))
    onFinished('accepted')
  }

  const handleReject = () => {
    if (!currentMap) return

    takeOverActionResponse({ mapId: currentMap.id, action: 'reject' })
    onFinished('rejected')
  }

  useEffect(() => {
    if (open && timeLeft !== undefined && timeLeft <= 0) {
      onFinished('canceled')
    }
  }, [open, timeLeft, onFinished])

  return (
    <Dialog open={open} size="small">
      <DialogHeader title={<FormattedMessage id="builder.takeOverResponseDialog.title" />} />
      <DialogContent>
        <Typography component="span" variant="body2" color="textSecondary">
          <FormattedMessage
            id="builder.takeOverResponseDialog.message1"
            values={{
              user: takeOverResponseState?.email,
              strong: (content) => <strong>{content}</strong>
            }}
          />
          {typeof timeLeft === 'number' && (
            <Box sx={{ mt: 2 }}>
              <Typography component="span" variant="body2" color="textSecondary">
                <FormattedMessage
                  id="builder.takeOverResponseDialog.message2"
                  values={{
                    timer: Math.ceil(timeLeft / 1000),
                    strong: (content) => <strong>{content}</strong>
                  }}
                />
              </Typography>
            </Box>
          )}
        </Typography>
      </DialogContent>
      <DialogFooter>
        <Button onClick={handleReject} disabled={isSaving} color="error">
          <FormattedMessage id="builder.takeOverResponseDialog.deny" />
        </Button>

        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          loading={isSaving}
          loadingPosition="start"
          startIcon={<CheckOutlined />}
        >
          <FormattedMessage id="builder.takeOverResponseDialog.approve" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default BuilderTakeOverResponseDialog
